import { inject, injectable } from "inversify";
import { VintomError } from "../../../api/model/error/VintomError";
import { FullPlayerConfiguration } from "../../../model/playerConfiguration/FullPlayerConfiguration";
import { TYPES } from "../../di/types";
import { ResourceLoader } from "../resourceLoader/ResourceLoader";

export interface PlayerLoader {
  loadResources(playerConfig: FullPlayerConfiguration): Promise<void>;
}

@injectable()
export class PlayerLoaderImpl implements PlayerLoader {
  constructor(@inject(TYPES.loader.resourceLoader.ResourceLoader) private resourceLoader: ResourceLoader) {}

  loadResources(playerConfig: FullPlayerConfiguration): Promise<void> {
    this.createPosterPreloadElements(playerConfig);

    return Promise.all([this.loadVideoJS(playerConfig), this.loadVideoLottie(playerConfig)]).then(() => Promise.resolve());
  }

  private createPosterPreloadElements(playerConfig: FullPlayerConfiguration): void {
    const allPosterUrls = playerConfig.scenarioElements
      .flatMap((scenarioElement) => [scenarioElement.poster, ...scenarioElement.ctas.map((cta) => cta.poster)])
      .filter((poster) => !!poster);

    for (const posterUrl of allPosterUrls) {
      if (document.querySelector(`link[rel=preload][href="${posterUrl}"]`)) {
        continue;
      }

      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = posterUrl;

      document.head.appendChild(link);
    }
  }

  private loadVideoJS(playerConfig: FullPlayerConfiguration): Promise<void> {
    if (!playerConfig.hasScenarioElementWithVideo || playerConfig.status !== "READY") {
      return Promise.resolve();
    }
    // @ts-ignore
    window.VIDEOJS_NO_DYNAMIC_STYLE = true;
    return this.resourceLoader
      .loadResources([{ location: playerConfig.videoJSUrl, type: "JS" }])
      .catch(() => Promise.reject(new VintomError("resourceLoadingFailure", "Failed to load videojs")));
  }

  private loadVideoLottie(playerConfig: FullPlayerConfiguration): Promise<void> {
    if (!playerConfig.hasScenarioElementWithAnimation || playerConfig.status !== "READY") {
      return Promise.resolve();
    }
    return this.resourceLoader
      .loadResources([{ location: playerConfig.lottieUrl, type: "JS" }])
      .catch(() => Promise.reject(new VintomError("resourceLoadingFailure", "Failed to load lottie-web")));
  }
}
